import api from "./api";
import { uploadInChunks } from "./upload";
import { getPhoto, deleteMedia, voiceKey } from "./photoStore";

// Text cheers written while offline wait here and are re-sent on the `online` event.
const KEY = "tycc_cheer_queue";
export const QUEUE_EVENT = "tycc-cheer-queue"; // fired on every queue change
export const SENT_EVENT = "tycc-cheer-sent"; // detail: { localId, cheer }
export const PROGRESS_EVENT = "tycc-cheer-progress"; // detail: { localId, percent | null }
export const FLUSH_EVENT = "tycc-cheer-flush"; // detail: { active }

export const readCheerQueue = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const write = (list) => { localStorage.setItem(KEY, JSON.stringify(list)); window.dispatchEvent(new Event(QUEUE_EVENT)); };

export const queueCheer = (item) => { write([...readCheerQueue(), item]); return item; };
export const dropQueuedCheer = (localId) => write(readCheerQueue().filter((q) => q.localId !== localId));
export const queuedFor = (childId) => readCheerQueue().filter((q) => q.kind !== "reply" && q.childId === childId);

export const queuedRepliesFor = (childId) => readCheerQueue().filter((q) => q.kind === "reply" && q.childId === childId);
export const dropQueuedReply = (childId, cheerId) => write(readCheerQueue().filter((q) => !(q.kind === "reply" && q.childId === childId && q.cheerId === cheerId)));

export const oldestQueuedAt = () => { const t = readCheerQueue().map((q) => new Date(q.createdAt).getTime()).filter(Boolean); return t.length ? Math.min(...t) : null; };

let flushing = false;
const patchQueued = (localId, patch) => write(readCheerQueue().map((q) => (q.localId === localId ? { ...q, ...patch } : q)));
const progress = (localId, percent) => window.dispatchEvent(new CustomEvent(PROGRESS_EVENT, { detail: { localId, percent } }));
const uploadStored = async (key, fallbackName, localId, from, to) => {
  const stored = await getPhoto(key);
  if (!stored?.blob) return null;
  const file = new File([stored.blob], stored.name || fallbackName, { type: stored.type || "application/octet-stream" });
  return uploadInChunks(file, (p) => progress(localId, Math.round(from + ((to - from) * p) / 100)));
};

// Text posts first (serverId remembered so a retry never duplicates it), then any offline photo / voice uploads and attaches.
const sendQueued = async (q) => {
  const hasMedia = q.hasPhoto || q.hasVoice;
  if (q.kind === "reply") {
    let reply = null;
    if (q.text && !q.textSent) { reply = (await api.put(`/children/${q.childId}/cheers/${q.cheerId}/reply`, { text: q.text })).data; if (q.hasVoice) patchQueued(q.localId, { textSent: true }); }
    if (q.hasVoice) {
      progress(q.localId, 0);
      const uploadId = await uploadStored(voiceKey(q.localId), "voice-note.webm", q.localId, 0, 100);
      if (uploadId) reply = (await api.post(`/children/${q.childId}/cheers/${q.cheerId}/reply/voice`, { upload_id: uploadId })).data;
      await deleteMedia(q.localId).catch(() => {});
    }
    return reply || (await api.get(`/children/${q.childId}/cheers`)).data.find((c) => c.id === q.cheerId);
  }
  let cheer;
  if (q.serverId) cheer = { id: q.serverId };
  else {
    cheer = (await api.post(`/children/${q.childId}/cheers`, { text: q.text })).data;
    if (hasMedia) patchQueued(q.localId, { serverId: cheer.id });
  }
  if (hasMedia) {
    progress(q.localId, 0);
    const split = q.hasPhoto && q.hasVoice ? 60 : 100;
    if (q.hasPhoto && !q.photoSent) {
      const uploadId = await uploadStored(q.localId, "photo.jpg", q.localId, 0, split);
      if (uploadId) cheer = (await api.post(`/children/${q.childId}/cheers/${cheer.id}/photo`, { upload_id: uploadId })).data;
      patchQueued(q.localId, { photoSent: true });
    }
    if (q.hasVoice) {
      const uploadId = await uploadStored(voiceKey(q.localId), "voice-note.webm", q.localId, q.hasPhoto ? split : 0, 100);
      if (uploadId) cheer = (await api.post(`/children/${q.childId}/cheers/${cheer.id}/voice`, { upload_id: uploadId })).data;
    }
    if (!cheer.text) cheer = (await api.get(`/children/${q.childId}/cheers`)).data.find((c) => c.id === cheer.id) || cheer;
    await deleteMedia(q.localId).catch(() => {});
  }
  return cheer;
};

export const flushCheerQueue = async () => {
  if (flushing || !localStorage.getItem("tycc_token")) return 0;
  flushing = true;
  window.dispatchEvent(new CustomEvent(FLUSH_EVENT, { detail: { active: true } }));
  let sent = 0;
  try {
    for (const q of readCheerQueue()) {
      try {
        const cheer = await sendQueued(q);
        dropQueuedCheer(q.localId);
        window.dispatchEvent(new CustomEvent(SENT_EVENT, { detail: { localId: q.localId, kind: q.kind || "cheer", cheerId: q.cheerId, childId: q.childId, childName: q.childName, hasPhoto: q.hasPhoto, hasVoice: q.hasVoice, cheer } }));
        sent += 1;
      } catch (err) {
        progress(q.localId, null);
        if (!err?.response) break; // still offline - keep the rest for later
        dropQueuedCheer(q.localId); // server rejected it (child gone / no access): don't retry forever
        if (q.hasPhoto || q.hasVoice) deleteMedia(q.localId).catch(() => {});
      }
    }
  } finally { flushing = false; window.dispatchEvent(new CustomEvent(FLUSH_EVENT, { detail: { active: false } })); }
  return sent;
};
